import { useCallback, useEffect, useMemo, useState } from 'react';
import type { UserType, UserIncomeReportRowType } from '@econtractor/types';
import * as apiReport from 'api/apiReports';
import { useToast } from 'context/ToastContext';
import { useTranslation } from 'react-i18next';
import type { ReportDateFilterState } from '../../helper/reportObjectsStorage';
import {
    loadReportUsersFilters,
    createDefaultReportUsersFilters,
    saveReportUsersFilters,
    type ReportUsersObjectFilter,
    type ReportUsersViewMode,
} from '../../helper/reportUsersStorage';
import { filterUsersByObject } from './filterByObject';

export const useReportUsers = () => {
    const { t } = useTranslation(['Reports']);
    const { showToast } = useToast();
    const savedFilters = useMemo(() => loadReportUsersFilters(), []);

    const [reload, setReload] = useState(0);
    const [executor, setExecutor] = useState<UserType | null>(savedFilters.executor);
    const [users, setUsers] = useState<UserIncomeReportRowType[] | null>(null);
    const [isReporting, setIsReporting] = useState(false);
    const [dates, setDates] = useState<[number, number] | null>(savedFilters.dates);
    const [viewMode, setViewMode] = useState<ReportUsersViewMode>(savedFilters.viewMode);
    const [objectFilter, setObjectFilter] = useState<ReportUsersObjectFilter>(savedFilters.objectFilter);
    const [dateFilter, setDateFilter] = useState<ReportDateFilterState>({
        period: savedFilters.period,
        dates: savedFilters.dates,
        customRange: savedFilters.customRange,
    });

    const fromMs = dates?.[0] ?? null;
    const toMs = dates?.[1] ?? null;

    const persist = useCallback(
        (patch: Partial<{
            executor: UserType | null;
            dateFilter: ReportDateFilterState;
            viewMode: ReportUsersViewMode;
            objectFilter: ReportUsersObjectFilter;
        }>) => {
            const nextFilter = patch.dateFilter ?? dateFilter;
            const nextView = patch.viewMode ?? viewMode;
            const nextObjectFilter = patch.objectFilter ?? objectFilter;
            if (!nextFilter.dates?.[0] || !nextFilter.dates?.[1]) return;
            saveReportUsersFilters({
                executor: patch.executor !== undefined ? patch.executor : executor,
                period: nextFilter.period,
                dates: [nextFilter.dates[0], nextFilter.dates[1]],
                customRange: nextFilter.customRange,
                viewMode: nextView,
                objectFilter: nextObjectFilter,
            });
        },
        [executor, dateFilter, viewMode, objectFilter],
    );

    const handleDateFilterChange = (filter: ReportDateFilterState) => {
        setDateFilter(filter);
        setDates(filter.dates);
        persist({ dateFilter: filter });
    };

    const handleViewModeToggle = () => {
        const next: ReportUsersViewMode = viewMode === 'detail' ? 'summary' : 'detail';
        setViewMode(next);
        persist({ viewMode: next });
    };

    const applyFilters = (next: { objectFilter: ReportUsersObjectFilter; executor: UserType | null }) => {
        setObjectFilter(next.objectFilter);
        setExecutor(next.executor);
        persist(next);
    };

    const refreshReport = () => setReload((previous) => previous + 1);
    const resetReport = () => {
        const defaults = createDefaultReportUsersFilters();
        setExecutor(defaults.executor);
        setDates(defaults.dates);
        setDateFilter({ period: defaults.period, dates: defaults.dates, customRange: defaults.customRange });
        setViewMode(defaults.viewMode);
        setObjectFilter(defaults.objectFilter);
        saveReportUsersFilters(defaults);
        refreshReport();
    };

    useEffect(() => {
        const load = async () => {
            if (!fromMs || !toMs) return;
            setIsReporting(true);
            const resRep = await apiReport.get4Users(fromMs, toMs);
            if (!resRep) showToast(t('common:messError'), 'danger');
            setUsers(resRep?.users ?? []);
            setIsReporting(false);
        };
        void load();
        // eslint-disable-next-line react-hooks/exhaustive-deps -- refetch on period changes or an explicit refresh
    }, [fromMs, toMs, reload]);

    const displayedUsers = useMemo(
        () => (users ? filterUsersByObject(users, objectFilter).filter((user) => !executor || Number(user.userId) === Number(executor.ID)) : null),
        [users, objectFilter, executor],
    );

    return {
        users: displayedUsers,
        isReporting,
        dates,
        setDates,
        dateFilter,
        viewMode,
        objectFilter,
        handleDateFilterChange,
        handleViewModeToggle,
        executor,
        applyFilters,
        refreshReport,
        resetReport,
    };
};
